import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url';

const TEXT_SNIPPET_LIMIT = 12000;

function fileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function isTextLike(file: File): boolean {
  const mime = file.type.toLowerCase();
  const extension = fileExtension(file.name);
  return (
    mime.startsWith('text/')
    || mime === 'application/json'
    || mime === 'application/xml'
    || ['csv', 'json', 'md', 'rtf', 'txt', 'xml'].includes(extension)
  );
}

function trimSnippet(value: string): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized.length > TEXT_SNIPPET_LIMIT
    ? `${normalized.slice(0, TEXT_SNIPPET_LIMIT - 1).trim()}...`
    : normalized;
}

async function extractPdfText(file: File): Promise<string | null> {
  const pdfjsLib = await import('pdfjs-dist');
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  }

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const pageTexts: string[] = [];
  const pageLimit = Math.min(pdf.numPages, 12);
  try {
    for (let pageNumber = 1; pageNumber <= pageLimit; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const text = content.items
        .map((item: unknown) => {
          if (item && typeof item === 'object' && 'str' in item) {
            return String((item as { str?: unknown }).str ?? '');
          }
          return '';
        })
        .filter(Boolean)
        .join(' ');
      if (text.trim()) pageTexts.push(text);
    }
  } finally {
    pdf.destroy?.();
  }

  const normalized = trimSnippet(pageTexts.join('\n'));
  return normalized || null;
}

export async function extractDocumentTextFromFile(file: File): Promise<string | null> {
  const extension = fileExtension(file.name);
  if (file.type === 'application/pdf' || extension === 'pdf') {
    try {
      return await extractPdfText(file);
    } catch {
      return null;
    }
  }

  if (!isTextLike(file)) {
    return null;
  }

  try {
    return trimSnippet(await file.text()) || null;
  } catch {
    return null;
  }
}
